import {
  Modal,
  Button,
  Table,
  Typography,
  useToast,
  Input,
  Form,
  Alert,
  AlertDescription,
} from '@crisellevaldez/speculo-ui';
import { ConfirmationDialog } from '../../common/dialogs/ConfirmationDialog';
import { OffsetPagination } from '../../common/OffsetPagination';
import { TableCard } from '../../common/TableCard';
import {
  RotateCw,
  Trash,
  UserCheck,
  Pencil,
  UserPlus,
  Copy,
  ExternalLink,
  Send,
} from 'lucide-react';
// TeamRosterWebAccountButton disabled — pending backend support for firebaseAuthId lookup
// import { TeamRosterWebAccountButton } from './TeamRosterWebAccountButton';
import { useTenant } from '../../../hooks/client/tenant/useTenant';
import { isProduction, isUat } from '../../../config/environment';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { useUser } from '../../../hooks/useUser';
import { useAuthStore } from '../../../contexts/auth/useAuthStore';
import { formatPhoneNumber } from '../../../utils/formatters';
import { deleteUser, inviteUser, updateUser, resendInvitesBulk } from '../../../api/remiApi';
import { inviteUserSchema, InviteUserFormData } from '../../../schemas/inviteUser.schema';
import { EditTeamMemberModal } from './EditTeamMemberModal';
import { TeamMemberInput } from '../../../schemas/teamMember.schema';
import type { Organization } from '../../../types/organization.types';

interface TeamRosterModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
}

type UserRow = {
  id: string;
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  phone_number: string;
  email: string;
  photoUrl: string;
  bio?: string;
  slug?: string;
  status: string;
  isActive: boolean;
  hasWebAccount?: boolean;
  acceptedAt: string | null;
};

export const TeamRosterModal = ({ isOpen, onClose, selectedOrg }: TeamRosterModalProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState<string>('firstName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const PAGE_SIZE = 25;
  const { showToast } = useToast();

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(0);
      setSelectedUserIds([]);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Selection is scoped to the visible page; changing the page would leave
  // stale ids selected, so clear it on any page move.
  const changePage = (page: number) => {
    setCurrentPage(page);
    setSelectedUserIds([]);
  };

  const { organization: storeOrg } = useOrganizationStore();
  const org = selectedOrg ?? storeOrg;
  const { data: tenant } = useTenant(org?.id, isOpen);
  const hasDomain = !!tenant?.domain?.primary;

  const { users, total, loading, refetch } = useOrganizationUsers({
    organizationId: org?.id,
    search: debouncedSearch || undefined,
    pageSize: PAGE_SIZE,
    offset: currentPage * PAGE_SIZE,
    enabled: isOpen,
    sortBy: sortField,
    sortOrder: sortDirection.toUpperCase() as 'ASC' | 'DESC',
  });

  const handleSort = (key: string, direction: 'asc' | 'desc') => {
    setSortField(key);
    setSortDirection(direction);
    changePage(0);
  };
  const { user: currentUser } = useUser();
  const { signOut } = useAuthStore();
  // Changing an active user's email changes their sign-in address. We hold the
  // pending edit and warn before saving: if it's your own email you're signed
  // out afterwards; for another active user it changes the email they log in
  // with. Pending users (not yet signed up) skip the warning.
  const [pendingEmailEdit, setPendingEmailEdit] = useState<TeamMemberInput | null>(null);
  const [isEmailEditSubmitting, setIsEmailEditSubmitting] = useState(false);

  // Resend only applies to users who haven't completed signup, so active
  // users are never selectable.
  const pendingIds = users.filter((user) => !user.isActive).map((user) => user.id);
  const selectedPendingUsers = users.filter(
    (user) => selectedUserIds.includes(user.id) && !user.isActive
  );
  const selectedPendingIds = selectedPendingUsers.map((user) => user.id);

  const handleSelectRows = (rows: string[]) => {
    // The header select-all reports every row id. Since active rows are
    // stripped from the selection, it can never see "all selected" and would
    // keep re-selecting instead of clearing — so when all pending users are
    // already selected, treat it as a deselect-all.
    const isSelectAll = rows.length === users.length && users.length > 0;
    const allPendingSelected =
      pendingIds.length > 0 && selectedPendingIds.length === pendingIds.length;
    if (isSelectAll && allPendingSelected) {
      setSelectedUserIds([]);
      return;
    }
    setSelectedUserIds(rows.filter((id) => pendingIds.includes(id)));
  };

  const getInviteUrl = (email: string) => {
    const baseUrl = isProduction()
      ? 'https://app.speculo.ai'
      : isUat()
        ? 'https://speculo-uat.web.app'
        : 'https://speculo-staging.web.app';
    const params = new URLSearchParams({ org: org?.id ?? '', email });
    return `${baseUrl}/signup?${params.toString()}`;
  };

  const handleCopyInviteUrl = (user: UserRow) => {
    navigator.clipboard.writeText(getInviteUrl(user.email)).then(() => {
      showToast({ message: 'Invite URL copied to clipboard', variant: 'success' });
    });
  };

  const inviteForm = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
  });

  const inviteMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!org?.id) throw new Error('Organization ID is required');
      try {
        await inviteUser(email, org.id);
      } catch (error) {
        const e = error as { response?: { data?: { detail?: string } }; message?: string };
        const message = e?.response?.data?.detail || e?.message || 'Failed to invite user';
        throw new Error(message);
      }
    },
    onSuccess: () => refetch(),
  });

  const resendMutation = useMutation({
    mutationFn: async (userIds: string[]) => {
      if (!org?.id) throw new Error('Organization ID is required');
      return resendInvitesBulk(userIds, org.id);
    },
    onSuccess: () => refetch(),
  });

  const handleResendConfirm = async () => {
    try {
      const { sent, skipped, failed } = await resendMutation.mutateAsync(selectedPendingIds);
      setShowResendDialog(false);
      setSelectedUserIds([]);
      const summary = [
        `${sent} sent`,
        ...(skipped > 0 ? [`${skipped} skipped`] : []),
        ...(failed > 0 ? [`${failed} failed`] : []),
      ].join(', ');
      showToast({
        message: `Invites resent: ${summary}`,
        variant: failed > 0 ? 'warning' : 'success',
      });
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to resend invites',
        variant: 'error',
      });
    }
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    setShowResendDialog(false);
    onClose();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleInviteSubmit = async (data: InviteUserFormData) => {
    try {
      await inviteMutation.mutateAsync(data.email.toLowerCase().trim());
      inviteForm.reset();
      showToast({ message: 'User invited successfully', variant: 'success' });
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to invite user',
        variant: 'error',
      });
    }
  };

  const handleEditSubmit = async (data: TeamMemberInput) => {
    if (!editingUser) return;

    // Changing an active user's email changes their sign-in address. Defer the
    // save and warn first; the update (and sign-out, if it's the current user)
    // runs once confirmed (see confirmEmailChange). Pending users skip this.
    const emailChanged = data.email.toLowerCase().trim() !== editingUser.email.toLowerCase().trim();
    if (emailChanged && editingUser.isActive) {
      setPendingEmailEdit(data);
      return;
    }

    setIsEditSubmitting(true);
    try {
      await updateUser(editingUser.id, {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phoneNumber,
        photoUrl: data.photoUrl,
        bio: data.bio,
        slug: data.slug,
      });
      await refetch();
      showToast({ message: 'User updated successfully', variant: 'success' });
      setShowEditModal(false);
      setEditingUser(null);
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update user',
        variant: 'error',
      });
    } finally {
      setIsEditSubmitting(false);
    }
  };

  // Save the confirmed active-user email change. Backend already updates the
  // auth identity's email. If the edited user is the current user, sign out so
  // they re-auth with the new address; otherwise just refresh the roster.
  const confirmEmailChange = async () => {
    if (!editingUser || !pendingEmailEdit) return;
    const data = pendingEmailEdit;
    const isSelf =
      currentUser?.email?.toLowerCase().trim() === editingUser.email?.toLowerCase().trim();
    setIsEmailEditSubmitting(true);
    try {
      await updateUser(editingUser.id, {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phoneNumber,
        photoUrl: data.photoUrl,
        bio: data.bio,
        slug: data.slug,
      });
      setPendingEmailEdit(null);
      setShowEditModal(false);
      setEditingUser(null);
      if (isSelf) {
        showToast({ message: 'Email updated. Please sign in again.', variant: 'info' });
        await signOut();
      } else {
        await refetch();
        showToast({ message: 'User updated successfully', variant: 'success' });
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update email',
        variant: 'error',
      });
    } finally {
      // Always clear loading so a later email-change confirm in this still-
      // mounted modal doesn't open stuck in the loading state.
      setIsEmailEditSubmitting(false);
    }
  };

  const tableColumns = [
    {
      id: 'photo',
      header: 'PHOTO',
      key: 'photoUrl',
      width: '70px',
      isCentered: true,
      cell: (row: { photoUrl: string; firstName: string }) => (
        <div className="flex justify-center">
          {row.photoUrl ? (
            <div className="w-9 h-9 rounded-full overflow-hidden">
              <img src={row.photoUrl} alt={row.firstName} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
              <span className="text-muted-foreground text-xs">{row.firstName?.[0] ?? '?'}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'firstName',
      header: 'FIRST NAME',
      key: 'firstName',
      width: '120px',
      cell: (row: { firstName: string }) => row.firstName || '-',
    },
    {
      id: 'lastName',
      header: 'LAST NAME',
      key: 'lastName',
      width: '120px',
      cell: (row: { lastName: string }) => row.lastName || '-',
    },
    {
      id: 'email',
      header: 'EMAIL',
      key: 'email',
      width: '200px',
      cell: (row: { email: string }) => (
        <div className="truncate" title={row.email}>
          {row.email || '-'}
        </div>
      ),
    },
    {
      id: 'phoneNumber',
      header: 'PHONE',
      width: '150px',
      key: 'phone_number',
      cell: (row: { phone_number: string }) => (
        <div className="truncate">{formatPhoneNumber(row.phone_number) || '-'}</div>
      ),
    },
    {
      id: 'website',
      header: 'WEBSITE',
      key: 'website',
      width: '90px',
      isCentered: true,
      cell: (row: UserRow) => (
        <div className="flex justify-center">
          {hasDomain && tenant?.domain?.primary ? (
            <a
              href={`https://${tenant.domain.primary}/team/${row.slug || row.firstName?.toLowerCase() || ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primaryfortext hover:opacity-70 transition-opacity flex items-center"
              title={`View ${row.firstName}'s profile`}
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'SIGNED UP',
      key: 'isActive',
      width: '110px',
      isCentered: true,
      sortable: true,
      cell: (row: UserRow) => (
        <div className="flex justify-center">
          {row.isActive ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
              <UserCheck className="w-3 h-3" />
              Active
            </span>
          ) : (
            <span
              onClick={() => handleCopyInviteUrl(row)}
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full cursor-pointer hover:opacity-80"
              title="Click to copy invite URL"
            >
              <Copy className="w-3 h-3" />
              Pending
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'ACTIONS',
      key: 'actions',
      width: '140px',
      isCentered: true,
      cell: (item: UserRow) => {
        const isSelf = currentUser?.email?.toLowerCase() === item.email?.toLowerCase();
        return (
          <div className="flex items-center justify-center gap-1.5">
            {/* Web account creation disabled — pending backend support for firebaseAuthId lookup.
                Re-enable once backend exposes firebaseAuthId on User GQL or a server-side endpoint exists. */}
            {/* <TeamRosterWebAccountButton
              email={item.email}
              firstName={item.firstName}
              lastName={item.lastName}
              organizationId={org?.id || ''}
              hasWebAccount={item.hasWebAccount}
              isRegistered={item.isActive}
            /> */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingUser(item);
                setShowEditModal(true);
              }}
              className="p-2"
              title="Edit user"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedUser(item);
                setShowDeleteDialog(true);
              }}
              disabled={isSelf}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-300 dark:border-red-700/30 hover:border-red-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
              title={isSelf ? 'You cannot remove your own account' : 'Remove user'}
            >
              <Trash className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="xl" className="max-w-7xl">
        <Modal.Header>
          <Typography variant="h3">Users / Team Roster</Typography>
        </Modal.Header>

        <Modal.Body className="p-4 sm:p-8 overflow-hidden">
          <Alert variant="warning" className="mb-4">
            <AlertDescription>
              Only team members with a phone number will be available as transfer line options in
              flows.
            </AlertDescription>
          </Alert>

          <div className="flex items-center mb-4 gap-2">
            <Input
              placeholder="Search by name, email or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm flex-1 min-w-0"
            />
            <Button
              variant="outline"
              className="h-8 shrink-0"
              size="sm"
              onClick={handleRefresh}
              isLoading={isRefreshing}
              disabled={isRefreshing}
            >
              <RotateCw className={`w-4 h-4 sm:mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>

          {selectedUserIds.length > 0 && (
            <div className="flex justify-center md:justify-start items-center gap-4 p-3 border-b border-border">
              <span className="t-label">
                <span className="font-bold text-foreground">{selectedUserIds.length}</span> user
                {selectedUserIds.length > 1 ? 's' : ''} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResendDialog(true)}
                className="flex items-center gap-2 h-8"
                disabled={selectedPendingIds.length === 0 || resendMutation.isPending}
                title={
                  selectedPendingIds.length === 0
                    ? 'Only pending users can receive a resent invite'
                    : undefined
                }
              >
                <Send className="w-4 h-4" />
                Resend Invites ({selectedPendingIds.length})
              </Button>
            </div>
          )}

          <TableCard
            className="h-[50vh] sm:h-[55vh] md:h-[60vh] xl:h-[65vh] mb-2"
            footer={
              <OffsetPagination
                currentPage={currentPage}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onNextPage={() => changePage(currentPage + 1)}
                onPrevPage={() => changePage(currentPage - 1)}
                onPageChange={changePage}
                isLoading={loading}
                label="users"
              />
            }
          >
            <Table
              columns={tableColumns}
              data={users}
              keyExtractor={(item) => item.id}
              loading={loading}
              sortable
              onSort={handleSort}
              selectable={true}
              selectedRows={selectedUserIds}
              onSelectRows={handleSelectRows}
            />
          </TableCard>
        </Modal.Body>

        <Modal.Footer>
          <form
            onSubmit={inviteForm.handleSubmit(handleInviteSubmit)}
            className="w-full flex gap-3 items-start"
          >
            <UserPlus className="w-4 h-4 mt-2.5 text-muted-foreground shrink-0" />
            <div className="flex-1">
              <Form.Control error={inviteForm.formState.errors.email?.message}>
                <Input
                  {...inviteForm.register('email')}
                  type="text"
                  placeholder="Enter email address to invite"
                />
              </Form.Control>
            </div>
            <Button type="submit" className="h-9 shrink-0" isLoading={inviteMutation.isPending}>
              Invite User
            </Button>
          </form>
        </Modal.Footer>
      </Modal>

      {editingUser && (
        <EditTeamMemberModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          onSubmit={handleEditSubmit}
          isSubmitting={isEditSubmitting}
          initialData={{
            firstName: editingUser.firstName,
            lastName: editingUser.lastName,
            phoneNumber: editingUser.phone_number,
            email: editingUser.email,
            photoUrl: editingUser.photoUrl,
            bio: editingUser.bio,
            slug: editingUser.slug,
          }}
        />
      )}

      <ConfirmationDialog
        isOpen={showResendDialog}
        onClose={() => setShowResendDialog(false)}
        onConfirm={handleResendConfirm}
        title="Resend Invites"
        message={
          <>
            Resend invitation emails to the following {selectedPendingIds.length} pending user
            {selectedPendingIds.length > 1 ? 's' : ''}? They will receive a new email with their
            registration link.
            <span className="block mt-3 max-h-40 overflow-y-auto rounded-md border border-border bg-muted/40 p-2">
              {selectedPendingUsers.map((user) => (
                <span key={user.id} className="block truncate text-sm" title={user.email}>
                  {user.email}
                </span>
              ))}
            </span>
          </>
        }
        confirmLabel="Resend Invites"
        variant="info"
        isLoading={resendMutation.isPending}
      />

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteDialog(false);
            setSelectedUser(null);
          }
        }}
        onConfirm={async () => {
          if (!selectedUser) return;
          setIsDeleting(true);
          try {
            await deleteUser(selectedUser.email);
            await refetch();
            showToast({ message: 'User removed successfully', variant: 'success' });
            setShowDeleteDialog(false);
            setSelectedUser(null);
          } catch (error) {
            showToast({
              message: error instanceof Error ? error.message : 'Failed to remove user',
              variant: 'error',
            });
          } finally {
            setIsDeleting(false);
          }
        }}
        title="Remove User"
        message={`Are you sure you want to remove ${selectedUser?.email}? This will revoke their access.`}
        confirmLabel="Remove"
        variant="danger"
        isLoading={isDeleting}
      />

      <ConfirmationDialog
        isOpen={!!pendingEmailEdit}
        onClose={() => {
          if (!isEmailEditSubmitting) setPendingEmailEdit(null);
        }}
        onConfirm={confirmEmailChange}
        title="Change email?"
        message={
          currentUser?.email?.toLowerCase().trim() === editingUser?.email?.toLowerCase().trim() ? (
            <>
              You're changing your own email to{' '}
              <span className="font-semibold text-foreground">{pendingEmailEdit?.email}</span>. This
              is your sign-in address, so you'll be signed out and need to log back in with the new
              email. Continue?
            </>
          ) : (
            <>
              Changing this user's email to{' '}
              <span className="font-semibold text-foreground">{pendingEmailEdit?.email}</span>{' '}
              updates their sign-in address, so they'll need to log in with the new email. Continue?
            </>
          )
        }
        confirmLabel={
          currentUser?.email?.toLowerCase().trim() === editingUser?.email?.toLowerCase().trim()
            ? 'Update & sign out'
            : 'Update email'
        }
        variant="warning"
        isLoading={isEmailEditSubmitting}
      />
    </>
  );
};
